import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import axios from 'axios';
import AdminLogin from '../components/admin/AdminLogin';
import AdminNav from '../components/admin/AdminNav';
import ResumenSetting from '../components/admin/setting/ResumenSetting';
import LeadsSetting from '../components/admin/setting/LeadsSetting';
import FichaLead from '../components/admin/setting/FichaLead';
import ActividadSetting from '../components/admin/setting/ActividadSetting';
import { getSesion } from '../utils/sesionAdmin';
import { settingGet, settingPost } from '../utils/settingApi';
import { textoSync } from '../utils/settingFormato';
import { ADMIN_API } from '../utils/adminApi';
import '../styles/admin.css';
import '../styles/admin-setting.css';

// /admin/setting: el CRM de setting en línea (spec 2026-10-23-crm-setting-design en el repo au-setting-workspace).
// Lo ven admin y setter. Se refresca solo cada 60 segundos; si una vuelta falla, deja lo último que llegó y avisa.
export const POLL_MS = 60 * 1000;

const PESTANAS = [['resumen', 'Resumen'], ['leads', 'Leads'], ['actividad', 'Actividad']];

const AdminSettingPage = () => {
  const [stage, setStage] = useState('checking'); // 'checking' | 'login' | 'viewer' | 'error' | 'denegado'
  const [sesion, setSesion] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [leadsData, setLeadsData] = useState(null);
  const [aviso, setAviso] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [pestana, setPestana] = useState('resumen');
  const [abierto, setAbierto] = useState(null); // conv_id de la ficha abierta
  const origenFicha = useRef(null); // la tarjeta que abrió la ficha: el foco vuelve ahí al cerrarla
  const [vuelta, setVuelta] = useState(0); // sube en cada refresco: Actividad vuelve a pedir lo suyo
  const hayDatos = useRef(false);
  // Cada carga lleva un turno. `enVuelo` es el turno que está esperando respuesta (0 = ninguno): el refresco de 60 s
  // no lanza otra encima de una lenta, y una carga a pedido (Recargar, la meta, volver a la pestaña) sí, pero deja
  // vieja a la anterior: una respuesta que llega tarde nunca pisa una más nueva.
  const turno = useRef(0);
  const enVuelo = useRef(0);

  const cargar = useCallback(async ({ forzar = false } = {}) => {
    if (enVuelo.current && !forzar) return;
    turno.current += 1;
    const mio = turno.current;
    enVuelo.current = mio;
    const vigente = () => mio === turno.current;
    try {
      const [r, l, s] = await Promise.all([settingGet('resumen'), settingGet('leads'), getSesion()]);
      if (!vigente()) return;
      setResumen(r);
      setLeadsData(l);
      setSesion(s);
      setAviso('');
      setVuelta((n) => n + 1);
      hayDatos.current = true;
      setStage('viewer');
    } catch (err) {
      if (!vigente()) return;
      const status = err?.response?.status;
      if (status === 401) {
        hayDatos.current = false;
        setStage('login');
        return;
      }
      if (status === 403) {
        // Igual que /admin/email: con el menú de lo que sí puede ver (la sesión se pide aparte, porque el 403 tumbó
        // el Promise.all) y sin un Reintentar que no sirve de nada.
        let ses = null;
        try { ses = await getSesion(); } catch (e) { /* sin menú, pero con el mensaje */ }
        if (!vigente()) return;
        setSesion(ses);
        hayDatos.current = false;
        setStage('denegado');
        return;
      }
      const msg = err?.response?.data?.error || 'No se pudo conectar con el servidor.';
      if (hayDatos.current) {
        setAviso(`${msg} Muestro lo último que llegó.`);
      } else {
        setErrMsg(msg);
        setStage('error');
      }
    } finally {
      if (enVuelo.current === mio) enVuelo.current = 0;
    }
  }, []);

  const recargar = () => cargar({ forzar: true });

  useEffect(() => { cargar(); }, [cargar]);
  // Refresco cada 60 s solo con la pestaña a la vista: oculta no pide nada; al volver, carga una vez al toque y
  // retoma el ritmo.
  useEffect(() => {
    if (stage !== 'viewer') return undefined;
    let id = null;
    const prender = () => { if (id === null) id = setInterval(() => cargar(), POLL_MS); };
    const apagar = () => { if (id !== null) { clearInterval(id); id = null; } };
    const cambio = () => {
      if (document.visibilityState === 'hidden') {
        apagar();
      } else {
        cargar({ forzar: true });
        prender();
      }
    };
    if (document.visibilityState !== 'hidden') prender();
    document.addEventListener('visibilitychange', cambio);
    return () => {
      apagar();
      document.removeEventListener('visibilitychange', cambio);
    };
  }, [stage, cargar]);

  // Sesión vencida (401) en cualquier parte, incluida la ficha: al login, y lo que estaba en vuelo queda viejo.
  const aLogin = () => {
    turno.current += 1;
    enVuelo.current = 0;
    hayDatos.current = false;
    setAbierto(null);
    setStage('login');
  };

  const logout = async () => {
    try {
      await axios.post(`${ADMIN_API}/api/admin/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Igual se sale: la cookie vence sola.
    }
    aLogin();
  };

  const abrirFicha = (convId, tarjeta) => {
    origenFicha.current = tarjeta || null;
    setAbierto(convId);
  };

  // El cambio recién pedido se ve como pendiente al toque, sin esperar el refresco de 60 s.
  const agregarPendiente = (cambio) => setLeadsData((prev) => {
    const pendientes = prev?.pendientes || {};
    return { ...prev, pendientes: { ...pendientes, [cambio.conv_id]: [...(pendientes[cambio.conv_id] || []), cambio] } };
  });

  const guardarMeta = async (n) => {
    await settingPost('config/meta', { meta_semanal: n });
    await recargar();
  };

  if (stage === 'checking') {
    return <div className="au-admin au-admin-shell"><div className="au-week-empty mono">&gt;&gt; Cargando el setting…</div></div>;
  }
  if (stage === 'login') {
    return <AdminLogin onSuccess={() => { setStage('checking'); recargar(); }} />;
  }
  if (stage === 'denegado') {
    return (
      <>
        <div className="au-admin au-admin-nav-wrap">
          <AdminNav actual="setting" rol={sesion?.rol} />
        </div>
        <div className="au-admin au-admin-shell">
          <div className="au-week-empty">&gt;&gt; No tienes acceso a esta sección. Usa el menú de arriba para ir a las tuyas.</div>
        </div>
      </>
    );
  }
  if (stage === 'error') {
    return (
      <div className="au-admin au-admin-shell">
        <div className="au-week-empty mono">
          &gt;&gt; {errMsg}{' '}
          <button type="button" className="au-set-btn" onClick={() => { setStage('checking'); recargar(); }}>Reintentar</button>
        </div>
      </div>
    );
  }

  const esAdmin = sesion?.rol === 'admin';
  const cuantos = leadsData?.leads?.length ?? 0;
  return (
    <>
      <div className="au-admin au-admin-nav-wrap">
        <AdminNav actual="setting" rol={sesion?.rol} />
      </div>
      {/* Mismo contenedor que /admin/leads: el fondo oscuro va a todo el ancho y el ancho máximo, adentro. Con el
          max-width en el mismo .au-admin, sobre 1400 px quedaban franjas blancas a los lados. */}
      <div className="au-admin au-admin-shell">
        <div className="au-set">
          <div className="au-admin-header">
            <div className="brand">AU &gt;&gt;<span className="subtitle">Setting</span></div>
            <div className="actions">
              <button type="button" onClick={recargar}>Recargar</button>
              <button type="button" onClick={logout}>Salir</button>
            </div>
          </div>
          <p className="au-set-sync">{`${textoSync(resumen?.sync)} · ${cuantos} leads`}</p>
          {aviso && <div className="au-admin-nota au-set-aviso" role="status">{aviso}</div>}
          <div className="au-set-pestanas" role="tablist">
            {PESTANAS.map(([clave, etiqueta]) => (
              <button
                key={clave}
                type="button"
                role="tab"
                aria-selected={pestana === clave}
                className={`au-set-pestana${pestana === clave ? ' is-activa' : ''}`}
                onClick={() => setPestana(clave)}
              >
                {etiqueta}
              </button>
            ))}
          </div>
          {pestana === 'resumen' && <ResumenSetting resumen={resumen} esAdmin={esAdmin} onGuardarMeta={guardarMeta} />}
          {pestana === 'leads' && (
            <LeadsSetting leads={leadsData?.leads || []} pendientes={leadsData?.pendientes || {}} onAbrir={abrirFicha} />
          )}
          {pestana === 'actividad' && (
            <ActividadSetting
              esAdmin={esAdmin}
              vuelta={vuelta}
              usuarios={Object.fromEntries((leadsData?.leads || []).map((l) => [l.conv_id, l.usuario]))}
            />
          )}
        </div>
      </div>
      {abierto && (
        <FichaLead
          convId={abierto}
          pendientes={(leadsData?.pendientes || {})[abierto] || []}
          onCambio={agregarPendiente}
          onCerrar={() => setAbierto(null)}
          onSinSesion={aLogin}
          origen={origenFicha.current}
        />
      )}
    </>
  );
};

export default AdminSettingPage;
